import { useEffect, useRef } from 'react';

/** Is the browser tab the one the person is looking at? */
export const tabInView = (): boolean => document.visibilityState === 'visible';

/**
 * Freshness without a socket: call `ask` every `everyMs` while the browser tab is in
 * view, and once more the moment the person comes back to the tab. Never while the tab
 * is hidden: a tab left open in the background costs the server nothing.
 *
 * It does not make the first call. Whoever uses it loads once on its own and turns
 * this on (`on`) when there is something to keep fresh.
 */
export function useInViewPoll(ask: () => void, everyMs: number, on: boolean): void {
  // The latest `ask` in a ref, so a new function identity never restarts the clock.
  const askRef = useRef(ask);
  useEffect(() => { askRef.current = ask; }, [ask]);

  useEffect(() => {
    if (!on) return undefined;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => askRef.current(), everyMs);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onViewChange = () => {
      if (!tabInView()) { stop(); return; }
      // Back in view: what is on screen may be old, so ask now and start the clock afresh.
      stop();
      askRef.current();
      start();
    };
    if (tabInView()) start();
    document.addEventListener('visibilitychange', onViewChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onViewChange);
    };
  }, [everyMs, on]);
}
